import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getStories } from '../services/api';
import StoryCard from '../components/StoryCard';
import { BookOpen, FileText } from 'lucide-react';

const Home = () => {
  const [featuredFiction, setFeaturedFiction] = useState([]);
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStories = async () => {
      try {
        const [fiction, articles] = await Promise.all([
          getStories('fiction'),
          getStories('article')
        ]);
        setFeaturedFiction(fiction.slice(0, 2));
        setFeaturedArticles(articles.slice(0, 2));
      } catch (error) {
        console.error('Error fetching stories:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStories();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-serif text-gray-900 mb-6 leading-tight">
            Stories Between
            <br />
            Memory and Reality
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
            Fiction and essays exploring the liminal spaces where loss transforms,
            memories become maps, and unfinished stories find their meaning.
          </p>
        </div>
      </section>

      {/* Featured Fiction */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-12">
            <div className="flex items-center gap-3">
              <BookOpen size={24} className="text-gray-900" />
              <h2 className="text-3xl font-serif text-gray-900">Recent Fiction</h2>
            </div>
            <Link
              to="/fiction"
              className="text-sm uppercase tracking-wider text-gray-600 hover:text-gray-900 transition-colors duration-200"
            >
              View All →
            </Link>
          </div>
          <div>
            {featuredFiction.map((story) => (
              <StoryCard key={story.id} story={story} />
            ))}
          </div>
        </div>
      </section>

      {/* Featured Articles */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-12">
            <div className="flex items-center gap-3">
              <FileText size={24} className="text-gray-900" />
              <h2 className="text-3xl font-serif text-gray-900">Recent Articles</h2>
            </div>
            <Link
              to="/articles"
              className="text-sm uppercase tracking-wider text-gray-600 hover:text-gray-900 transition-colors duration-200"
            >
              View All →
            </Link>
          </div>
          <div>
            {featuredArticles.map((article) => (
              <StoryCard key={article.id} story={article} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gray-900 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-serif mb-6">Stay Connected</h2>
          <p className="text-gray-300 leading-relaxed mb-8">
            Interested in reading more or getting in touch? I'd love to hear from you.
          </p>
          <Link
            to="/contact"
            className="inline-block px-8 py-3 bg-white text-gray-900 text-sm uppercase tracking-wider hover:bg-gray-100 transition-colors duration-200"
          >
            Get in Touch
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;